import { useEffect, useState } from "react";

import { ApiError } from "../services/http";
import {
  getLoadRun,
  listLoadRunsForRevision,
  type LoadRunChoice,
  type LoadRunDemandFigures,
} from "../services/loadLink";
import { formatQuantity } from "../utils/formatQuantity";

// The load-run selector of the power quality studies (EOS-11, A32), the twin of the shared
// fault-run selector (A27 (e)): it lists the EOS-02 load runs of the active revision, drops a
// chosen run the revision no longer holds, reads the chosen run's stored demand figures for
// display only (the backend reads the same frozen result again when the study is saved),
// and says when the load run's own result is not VALID.

/** The first option of the selector: nothing is chosen for the engineer. */
const CHOOSE = "Choose…";

export const NO_OPEN_REVISION_LOAD_NOTE =
  "Select an open project revision in the topbar to link a load run, or use typed figures.";

export const NO_LOAD_RUNS_NOTE = "This revision holds no load run yet.";

export const LOAD_RUN_NOT_LINKABLE =
  "This load run has no demand figures in its result and cannot be linked.";

export const LOAD_RUN_UNDER_REVIEW_NOTE =
  "The load run's own result is not valid; the link will be marked for review.";

// The words of a load run's status, as the load study page shows them.
const LOAD_STATUS_LABELS: Record<string, string> = {
  VALID: "Calculated",
  WARNING: "Calculated with warnings",
  REVIEW_REQUIRED: "Engineering review required",
};

/** The option text of one load run in the selector: "LG-MSB-01 · rev 1 · Calculated". */
export function loadRunOptionText(run: LoadRunChoice): string {
  const status = LOAD_STATUS_LABELS[run.design_check_status] ?? run.design_check_status;
  return `${run.calculation_key} · rev ${run.revision_number} · ${status}`;
}

/** The read-only line under the selector: the demand figures as the stored result holds them. */
export function linkedDemandText(figures: LoadRunDemandFigures): string {
  const p = formatQuantity(figures.demand_power_kw, "kW").display;
  const s = formatQuantity(figures.apparent_power_kva, "kVA").display;
  const q = formatQuantity(figures.reactive_power_kvar, "kvar").display;
  return `Group ${figures.group_code} · P ${p} · S ${s} · Q ${q}`;
}

/** The tooltip of that line: the exact engine decimals. */
export function linkedDemandTitle(figures: LoadRunDemandFigures): string {
  return [
    formatQuantity(figures.demand_power_kw, "kW").exact,
    formatQuantity(figures.apparent_power_kva, "kVA").exact,
    formatQuantity(figures.reactive_power_kvar, "kvar").exact,
  ]
    .filter(Boolean)
    .join(" · ");
}

type LoadRunSelectorProps = {
  /** The active open revision, or null (then only the note is shown). */
  revisionId: string | null;
  /** The run kept in the draft, "" when none. */
  loadRunId: string;
  onChange: (loadRunId: string) => void;
  /** The run the selector read, for a form that shows the figures itself. */
  onLinked?: (figures: LoadRunDemandFigures | null) => void;
};

export function LoadRunSelector({ revisionId, loadRunId, onChange, onLinked }: LoadRunSelectorProps) {
  const [loadRuns, setLoadRuns] = useState<LoadRunChoice[] | null>(null);
  const [loadRunsError, setLoadRunsError] = useState<string | null>(null);
  const [linked, setLinked] = useState<LoadRunDemandFigures | null>(null);
  const [linkedError, setLinkedError] = useState<string | null>(null);

  // The list follows the active revision; a revision without an open state lists nothing.
  useEffect(() => {
    if (revisionId === null) {
      setLoadRuns(null);
      setLoadRunsError(null);
      return;
    }
    const controller = new AbortController();
    setLoadRuns(null);
    setLoadRunsError(null);
    listLoadRunsForRevision(revisionId, controller.signal)
      .then((runs) => {
        if (!controller.signal.aborted) {
          setLoadRuns(runs);
        }
      })
      .catch((error: unknown) => {
        if (!controller.signal.aborted) {
          setLoadRunsError(
            error instanceof Error ? error.message : "The load runs could not be listed.",
          );
        }
      });
    return () => controller.abort();
  }, [revisionId]);

  // A run kept in the draft that the listed revision does not hold is no longer chosen.
  useEffect(() => {
    if (loadRuns !== null && loadRunId !== "" && !loadRuns.some((run) => run.id === loadRunId)) {
      onChange("");
    }
  }, [loadRuns, loadRunId, onChange]);

  useEffect(() => {
    onLinked?.(linked);
  }, [linked, onLinked]);

  // The chosen run's demand figures, read from its stored result for display only.
  useEffect(() => {
    if (loadRunId === "") {
      setLinked(null);
      setLinkedError(null);
      return;
    }
    const controller = new AbortController();
    setLinked(null);
    setLinkedError(null);
    getLoadRun(loadRunId, controller.signal)
      .then((figures) => {
        if (!controller.signal.aborted) {
          setLinked(figures);
        }
      })
      .catch((error: unknown) => {
        if (controller.signal.aborted) {
          return;
        }
        setLinkedError(error instanceof ApiError ? error.message : LOAD_RUN_NOT_LINKABLE);
      });
    return () => controller.abort();
  }, [loadRunId]);

  if (revisionId === null) {
    return <p data-no-revision-note="true">{NO_OPEN_REVISION_LOAD_NOTE}</p>;
  }

  return (
    <>
      <label>
        Load run
        <select name="loadRunId" value={loadRunId} onChange={(event) => onChange(event.target.value)}>
          <option value="">{CHOOSE}</option>
          {(loadRuns ?? []).map((run) => (
            <option key={run.id} value={run.id}>
              {loadRunOptionText(run)}
            </option>
          ))}
        </select>
      </label>
      {loadRuns !== null && loadRuns.length === 0 ? (
        <p data-no-load-runs-note="true">{NO_LOAD_RUNS_NOTE}</p>
      ) : null}
      {loadRunsError !== null ? <p role="alert">{loadRunsError}</p> : null}
      {linked !== null ? (
        <p data-linked-demand={linked.run_id} title={linkedDemandTitle(linked)}>
          {linkedDemandText(linked)}
        </p>
      ) : null}
      {linkedError !== null ? <p role="alert">{linkedError}</p> : null}
      {linked !== null && linked.status !== "VALID" ? (
        <p data-load-run-review-note="true">{LOAD_RUN_UNDER_REVIEW_NOTE}</p>
      ) : null}
    </>
  );
}

export default LoadRunSelector;
